import {UserCheck01Icon, UserLeft01Icon} from '@/components/ui/icon/Icon'
import {MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {PatientExtended} from '@/features/patients/types'
import {useTranslation} from '@/translations/i18n'

/**
 * Discharging a patient (status `inactive`) is allowed only while none of their projects is `open`
 * or `opening`. The BE enforces the same rule and answers 422 patient-has-active-projects, so the
 * disabled item is a hint, not the only guard.
 */
const hasActiveProject = (patient: PatientExtended) =>
    patient.project?.status == 'open' || patient.project?.status == 'opening'

export const PatientDischargeMenuItem = ({patient, onSelect}: {patient: PatientExtended; onSelect: () => void}) => {
    const {t} = useTranslation()
    const isDischarged = patient.status == 'inactive'

    // Death keeps its own dedicated flow: neither action is offered on a deceased patient.
    if (patient.status == 'deceased') {
        return null
    }

    const disabledReason = !isDischarged && hasActiveProject(patient) ? t('patients:discharge_disabled_tooltip') : null

    // No preventDefault here: the click has to close the dropdown before the dialog shows up.
    const menuItem = (
        <MenuItem
            disabled={!!disabledReason}
            style={disabledReason ? {pointerEvents: 'none'} : undefined}
            onClick={onSelect}
        >
            {isDischarged ? <UserCheck01Icon /> : <UserLeft01Icon />}
            {isDischarged ? t('patients:readmit_patient') : t('patients:discharge_patient')}
        </MenuItem>
    )

    if (!disabledReason) {
        return menuItem
    }

    return (
        <Tooltip variant="light" content={disabledReason} triggerProps={{asChild: true}}>
            {/* The span is the hover target: a disabled control can't reliably fire the
                pointer events Radix needs (canonical Radix pattern). */}
            <span style={{display: 'block', cursor: 'not-allowed'}}>{menuItem}</span>
        </Tooltip>
    )
}
